import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, Download, CheckCircle, Zap, Upload, Sparkles } from 'lucide-react';
import Layout from '../components/layout/Layout';

export default function GettingStartedPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Getting Started - Master Stock Photography | Metapic';
  }, []);

  const episodes = [
    {
      number: 1,
      title: 'Welcome & Course Overview',
      description: 'Phone vs camera, realistic expectations, and the microstock mindset',
    },
    {
      number: 2,
      title: 'Microstock Foundations',
      description: 'How royalty works, who buys stock photos, and what truly sells',
    },
    {
      number: 3,
      title: 'What You Can Sell',
      description: 'Profitable ideas you can shoot at home - lifestyle, objects, food, and AI images',
    },
    {
      number: 4,
      title: 'Phone Photography Techniques',
      description: 'Rule of thirds, negative space, and detail shots - instant results',
    },
    {
      number: 5,
      title: 'Edit, Fix, and Finalize',
      description: 'Complete mobile workflow using your phone\'s default editor',
    },
    {
      number: 6,
      title: 'Speeding Up Your Workflow',
      description: 'Metadata secrets, auto-generation tools, and faster uploading with Metapic',
    },
    {
      number: 7,
      title: 'Submitting & Quality Control',
      description: 'Platform overview, common rejections, and your first upload strategy',
    },
    {
      number: 8,
      title: 'Earning Faster & Scaling',
      description: 'Finding your niche, weekly workflows, and understanding analytics',
    },
    {
      number: 9,
      title: 'Bonus & Next Steps',
      description: 'Recommended gear, using Metapic credits, and what to do after your first sale',
    },
  ];

  const features = [
    {
      icon: Sparkles,
      title: 'Mobile-First Workflow',
      description: 'Shoot, edit, and upload stock photos entirely from your smartphone',
    },
    {
      icon: Zap,
      title: 'Auto Metadata Generation',
      description: 'Generate titles, descriptions, and 50 keywords instantly with Metapic',
    },
    {
      icon: Upload,
      title: 'Direct Platform Upload',
      description: 'Upload to Adobe Stock, Shutterstock, and others with digital model releases',
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
        {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
          <div className="absolute top-60 -left-40 w-80 h-80 bg-gradient-to-br from-orange-400/20 to-pink-400/20 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto mb-16">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-full mb-6">
              <Play className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-600">Free Video Series</span>
            </div>

            {/* Title */}
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent leading-tight">
              Mobile Microstock Photography
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Turn your smartphone into a passive income machine.
              <br />
              Complete course from beginner to earning your first sale.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <a
                href="https://www.youtube.com/playlist?list=PLPC3cdd45wMv4ue4HhQH2x8e0uinbpBvD"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 group"
              >
                <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                Watch Video Series
              </a>
              <Link
                to="/mobile-app"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-gray-50 text-gray-900 rounded-xl font-semibold border-2 border-gray-200 hover:border-gray-300 shadow-sm hover:shadow transition-all duration-200 group"
              >
                <Download className="w-5 h-5 group-hover:scale-110 transition-transform" />
                Download Mobile App
              </Link>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>9 Complete Modules</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Mobile-First Approach</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Beginner Friendly</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Series Section */}
      <section id="video-series" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              9-Module Complete Course
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From smartphone setup to earning passive income - everything you need in one place
            </p>
          </div>

          {/* YouTube Playlist Embed */}
          <div className="max-w-5xl mx-auto mb-16">
            <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-2xl shadow-2xl bg-gray-900">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/videoseries?list=PLPC3cdd45wMv4ue4HhQH2x8e0uinbpBvD"
                title="Photo Stock Journey Series"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>

          {/* Module List */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {episodes.map((episode) => (
              <div
                key={episode.number}
                className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
              >
                <div className="flex items-start gap-4 mb-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold shadow-md">
                    {episode.number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                      {episode.title}
                    </h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm line-clamp-2">
                  {episode.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Features Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500/10 to-pink-500/10 border border-orange-500/20 rounded-full mb-4">
              <Zap className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-600">Speed Up Your Workflow</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Automate Your Metadata
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Use Metapic mobile app to generate professional metadata in seconds,
              not hours
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-md">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Download CTA */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-12 text-center shadow-2xl">
            <h3 className="text-3xl font-bold text-white mb-4">
              Ready to Start Your Journey?
            </h3>
            <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
              Download Metapic mobile app and start generating professional metadata
              for your stock photos today.
            </p>
            <Link
              to="/mobile-app"
              className="inline-flex items-center gap-2 px-10 py-5 bg-white hover:bg-gray-50 text-gray-900 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-200 group"
            >
              <Download className="w-6 h-6 group-hover:scale-110 transition-transform" />
              Download Free App
            </Link>
            <p className="text-white/80 text-sm mt-6">
              Available for iOS and Android • No credit card required
            </p>
          </div>
        </div>
      </section>

      {/* Complete Course Curriculum */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Complete Course Curriculum
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              9 comprehensive modules covering everything from mindset to earning passive income
            </p>
          </div>

          <div className="space-y-8">
            {/* Module 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Welcome & Course Overview</h3>
                  <p className="text-blue-700 font-medium mb-4">Goal: Align mindset, lower the barrier to entry, and set realistic expectations</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Welcome to the Course & Meet Your Instructor</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Phone vs. Professional Camera: Why We Focus on Mobile</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>How This Course is Structured for Fast Results</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>Your Earning Potential: Setting Realistic Expectations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>How You Can Start Selling Today (The Microstock Mindset)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Module 2 */}
            <div className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Microstock Foundations: Understanding the Game</h3>
                  <p className="text-purple-700 font-medium mb-4">Goal: Ensure the student understands the core rules of commercial photography</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>What is Microstock and How the Royalty System Works</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Who Buys Stock Photos and Why (Identifying Your Target Buyer)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Commercial vs. Instagram Aesthetic: What Truly Sells</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Evergreen Content vs. Timely Trends (Building a Sustainable Portfolio)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Common Myths That Can Tank Your Submissions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span>Basic Legalities: Copyright and Why It Matters</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Module 3 */}
            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-green-600 to-green-700 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">What You Can Sell: Finding Profitable Ideas</h3>
                  <p className="text-green-700 font-medium mb-4">Goal: Open students' eyes to ideas they can immediately shoot at home</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Real-Life Sellers: The Top Categories by Demand</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Lifestyle & Daily Routines (The "Work from Home" Niche)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Simple Objects & Flat Lays (Minimalism and Concept)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Interior, Home, Workspace, and Family</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Food, Everyday Scenes, and Texture</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>The Human Element: Model Release Basics</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>AI Images for Microstock: Platform Policies & Pros vs Cons</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Module 4 */}
            <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-200 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  4
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Phone Photography: Techniques & Instant Results</h3>
                  <p className="text-orange-700 font-medium mb-4">Goal: Action-packed module with techniques that immediately create photos ready for editing</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span>Agency-Ready Phone Setup: Settings, Lens Cleaning, Max Resolution</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span>Technique 1: Rule of Thirds & Everyday Objects</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span>Technique 2: Negative/Empty Space for Advertising Copy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span>Technique 3: Detail, Texture, and Close-Up Focus</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      <span>Practical Tasks: Capture 9 photos using these proven techniques</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Module 5 */}
            <div className="bg-gradient-to-br from-pink-50 to-white border-2 border-pink-200 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-pink-600 to-pink-700 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  5
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Edit, Fix, and Finalize: Complete Mobile Workflow</h3>
                  <p className="text-pink-700 font-medium mb-4">CORE MODULE: Turning raw shots into agency-ready files using only your phone</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                      <span>The Stock Editing Philosophy (Correction, not Artistic Flair)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                      <span>Step 1: Quick Fixes - Crop & Straighten</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                      <span>Step 2: Lighting and Color Correction</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                      <span>Step 3: Quality Control - Noise, Artifacts, Sharpness</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                      <span>Finalization: Saving High-Resolution Files Ready for Submission</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Module 6 */}
            <div className="bg-gradient-to-br from-indigo-50 to-white border-2 border-indigo-200 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  6
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Speeding Up Your Workflow (Metadata & Tools)</h3>
                  <p className="text-indigo-700 font-medium mb-4">Goal: Save time and ensure photos are discoverable by buyers</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span>Why Metadata and Keywords are the Secret to Sales</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span>The Pain Point of Manual Uploading & Keyword Entry</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span>Introduction to Metapic Mobile App</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span>Auto Metadata Generation and Keyword Optimization</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span>Creating Digital Model Releases on the Go</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <span>Uploading Faster with Mobile-First Strategy</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Module 7 */}
            <div className="bg-gradient-to-br from-red-50 to-white border-2 border-red-200 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  7
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Submitting & Quality Control</h3>
                  <p className="text-red-700 font-medium mb-4">Goal: Practical steps to get that first photo approved</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <span>Platform Overview: Adobe Stock, Shutterstock, and More</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <span>The Golden Rules of Submission (Sizes, File Types, Naming)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <span>Common Rejection Reasons (And How to Prevent Them)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <span>Handling Rejections: Should You Re-edit or Abandon?</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <span>Your First Upload Strategy (The Batching Technique)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Module 8 */}
            <div className="bg-gradient-to-br from-teal-50 to-white border-2 border-teal-200 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  8
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Earning Faster & Scaling Consistency</h3>
                  <p className="text-teal-700 font-medium mb-4">Goal: Long-term strategy for passive income growth</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span>Finding Your Niche: Less Competitive & High-Paying Topics</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span>Quantity vs. Quality: Finding the Right Balance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span>Simple Weekly Workflow (Beginner & Pro Version)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span>How Successful Contributors Scale Their Portfolios</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                      <span>Understanding Your Analytics (What Sells and Why)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Module 9 */}
            <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-amber-600 to-amber-700 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  9
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Bonus & Next Steps</h3>
                  <p className="text-amber-700 font-medium mb-4">Goal: Strong closure and encouraging future action</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <span>Recommended Affordable Phone Photography Gear</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <span>How to Strategically Use Your Metapic Credits</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <span>What to Do After Your First Sale (Reinvesting in Growth)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <span>Where to Go Next in Your Microstock Journey</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
    </Layout>
  );
}
